/**
 * Help content para los paneles del CEO Executive Dashboard.
 * Usado por InfoButton en cada panel header.
 */
export const CEO_HELP_CONTENT = {
  populationOverview: {
    title: "Population Overview",
    description:
      "High-level view of the entire member population across all locations.",
    sections: [
      {
        heading: "Key Metrics",
        items: [
          "Total Members: Count of all unique members in the network",
          "Health Risk Distribution: Breakdown by health status (Well, Acute, Chronic, At Risk, Complex, End of Life)",
          "High Risk %: Percentage of members classified as At Risk, Complex, or End of Life",
          "ER Rate: Percentage of members with at least one ER event",
        ],
      },
    ],
  },

  costAnalysis: {
    title: "Cost Analysis",
    description:
      "Financial breakdown of total cost of care across the network, including cost distribution by type, top PCPs by cost, and top facilities.",
    sections: [
      {
        heading: "Cost Breakdown",
        items: [
          "Inpatient, Outpatient, ER, Professional, Pharmacy, Virtual Visit, and Ancillary costs",
          "Donut chart shows the proportional distribution of each cost type",
          "Hover over segments for detailed dollar amounts and percentages",
        ],
      },
      {
        heading: "Tables",
        items: [
          "Top PCPs by Cost: Providers generating the highest total cost of care",
          "Top Facilities: Healthcare facilities with highest claim costs",
          "Click 'Show All' to expand the full list",
        ],
      },
    ],
  },

  qualityMeasures: {
    title: "Quality Measures (HEDIS)",
    description:
      "HEDIS compliance tracking showing progress toward contractual quality targets.",
    sections: [
      {
        heading: "Understanding the Bars",
        items: [
          "Each bar shows the current compliance rate vs the HEDIS target",
          "On Track (green): Meeting or exceeding the target",
          "At Risk (amber): Within 10% of the target",
          "Behind (red): More than 10% below the target",
        ],
      },
      {
        heading: "Overall Compliance",
        items: [
          "The overall rate represents the percentage of all care gaps that have been closed",
          "Individual measures are sorted by number of open gaps (highest impact first)",
        ],
      },
    ],
  },

  centerPerformance: {
    title: "Center Performance Comparison",
    description:
      "Side-by-side comparison of all 11 medical centers across key performance indicators.",
    sections: [
      {
        heading: "Heatmap Colors",
        items: [
          "No Visit %: Green (<50%), Yellow (50-80%), Red (>80%)",
          "Gap Compliance: Green (>40%), Yellow (20-40%), Red (<20%)",
          "CM Ratio: Red if High Risk / In CM ratio exceeds 20:1",
        ],
      },
      {
        heading: "Alerts",
        items: [
          "Centers with critical alerts are highlighted in red",
          "Hover over alert icons for detailed information",
        ],
      },
    ],
  },

  trends: {
    title: "Trends & Top Performers",
    description:
      "Care gap closure trends over the last 18 months and top-performing providers.",
    sections: [
      {
        heading: "Trend Chart",
        items: [
          "Shows the number of care gaps closed per month",
          "Filtered to the last 18 months for readability",
          "Upward trends indicate improving quality performance",
        ],
      },
      {
        heading: "Top Providers",
        items: [
          "Ranked by total care gaps closed",
          "Engagement % shows the proportion of their patients seen within 12 months",
          "Green (>50%), Yellow (30-50%), Red (<30%)",
        ],
      },
    ],
  },

  codingRevenue: {
    title: "Coding Revenue Opportunity",
    description:
      "HCC coding recapture opportunities showing unaddressed conditions and revenue potential.",
    sections: [
      {
        heading: "Key Metrics",
        items: [
          "Total Opportunities: All HCC coding gaps identified",
          "Not Addressed: Percentage of opportunities not yet reviewed",
          "Condition Types: Dropped (lost from prior year), Suspect (algorithmically identified), Pharmacy (medication-based)",
        ],
      },
      {
        heading: "Recapture",
        items: [
          "Top HCCs: Most common unaddressed conditions (Diabetes, COPD, Heart Failure, etc.)",
          "Recapture by PCP: Shows each provider's recapture rate with a progress indicator",
        ],
      },
    ],
  },

  engagement: {
    title: "Engagement & Growth",
    description:
      "Patient engagement metrics, new member growth, and contact availability.",
    sections: [
      {
        heading: "Visit Engagement",
        items: [
          "Engagement Rate: % of members who visited within the last 12 months",
          "Breakdown by recency: <3 months, 3-6 months, 6-12 months, 12+ months",
        ],
      },
      {
        heading: "New Members",
        items: [
          "Total new members added to the network",
          "Growth rate as a percentage of total population",
          "Top locations by new member count",
        ],
      },
      {
        heading: "Contact Availability",
        items: [
          "Percentage of members with email, phone, and mobile on file",
          "Critical for outreach campaigns and patient engagement",
        ],
      },
    ],
  },
};
